import { Injectable } from '@angular/core';
import exifr from 'exifr';
import { Papa } from 'ngx-papaparse';
import piexifjs from 'piexifjs';
import {
  ALTITUDE_REF,
  FileMetadataInfo,
  PhotoExifData,
} from '../../../models/uploaded-image-metadata.model';

interface FileExifResult {
  coords?: { latitude: number; longitude: number };
  altitude?: number;
  exifData: PhotoExifData;
}

@Injectable({ providedIn: 'root' })
export class XlifService {
  private static readonly EXIF_TAGS = [
    'GPSAltitude',
    'GPSAltitudeRef',
    'FocalLength',
    'ISO',
    'ExposureTime',
    'ImageWidth',
    'ImageHeight',
    'ExifImageWidth',
    'ExifImageHeight',
    'Make',
    'Model',
    'DateTimeOriginal',
    'ExposureBiasValue',
  ];

  constructor(private papa: Papa) {}

  async getXlifDataFromFiles(photos: File[]) {
    const result = [];
    for (const photo of photos) {
      const metadata = await this.getFullMetadataFromFile(photo);
      result.push(
        new FileMetadataInfo(
          photo.name,
          metadata?.coords,
          metadata?.altitude,
          photo.size,
          metadata?.exifData,
        ),
      );
    }
    return result;
  }

  async getXlifDataFromFlatFile(csvFile: File) {
    const result: FileMetadataInfo[] = [];
    const parsedData = (await this.readCSV(csvFile)) as string[][];
    const indexes = this.flatFileIndexes(parsedData);

    if (indexes) {
      parsedData.forEach((element, index) => {
        if (index > indexes.headerIndex && element.length > 3) {
          result.push(
            new FileMetadataInfo(
              element[0],
              { latitude: +element[indexes.lat], longitude: +element[indexes.long] },
              +element[indexes.alt],
            ),
          );
        }
      });
    }
    return result;
  }

  private findIndex(row: string[], name: string) {
    return row.findIndex((item) => item.toLowerCase().includes(name));
  }

  private flatFileIndexes(data: string[][]) {
    const headerIndex = data.findIndex(
      (row) => row.length > 3 && row.join().toLowerCase().includes('long'),
    );
    if (headerIndex === -1) {
      return null;
    }
    return {
      headerIndex,
      lat: this.findIndex(data[headerIndex], 'lat'),
      long: this.findIndex(data[headerIndex], 'long'),
      alt: this.findIndex(data[headerIndex], 'alt'),
    };
  }

  async removeXlif(photo: File | undefined): Promise<File> {
    if (!photo) {
      return Promise.reject();
    }
    return new Promise((resolve, reject) => {
      const type = photo.type;
      const name = photo.name;
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (!result) {
          return reject();
        }
        const noMetadataImage = piexifjs.remove(result);
        fetch(noMetadataImage)
          .then((res) => {
            if (res.ok) {
              return res.blob();
            }
            reject();
          })
          .then((blob) => {
            if (!blob) {
              reject();
            } else {
              resolve(new File([blob], name, { type }));
            }
          });
      };
      reader.readAsDataURL(photo);
    });
  }

  private async readCSV(csvFile: File) {
    return new Promise((resolve) => {
      this.papa.parse(csvFile, {
        header: false,
        complete: (results) => {
          resolve(results.data);
        },
      });
    });
  }

  private async getFullMetadataFromFile(file: File): Promise<FileExifResult | null> {
    try {
      const [coords, parsed] = await Promise.all([
        exifr.gps(file).catch(() => null),
        exifr.parse(file, XlifService.EXIF_TAGS).catch(() => null),
      ]);

      const altitude = this.extractAltitude(parsed);
      const exifData = this.parseExifData(parsed);

      if (coords && altitude) {
        return { coords, altitude, exifData };
      }
      return { exifData };
    } catch {
      return null;
    }
  }

  private extractAltitude(parsed: Record<string, unknown> | null): number | undefined {
    if (!parsed) return undefined;
    const altitude = parsed['GPSAltitude'] as number | undefined;
    if (altitude == null) return undefined;
    const altitudeRef = Number(parsed['GPSAltitudeRef']);
    return altitudeRef === ALTITUDE_REF.BELOW_SEA_LEVEL ? -altitude : altitude;
  }

  private parseExifData(parsed: Record<string, unknown> | null): PhotoExifData {
    if (!parsed) return {};
    const width = (parsed['ExifImageWidth'] ?? parsed['ImageWidth']) as number | undefined;
    const height = (parsed['ExifImageHeight'] ?? parsed['ImageHeight']) as number | undefined;
    const captureDate = parsed['DateTimeOriginal'];
    return {
      focalLength: parsed['FocalLength'] as number | undefined,
      iso: parsed['ISO'] as number | undefined,
      exposureTime: parsed['ExposureTime'] as number | undefined,
      imageWidth: width,
      imageHeight: height,
      cameraMake: parsed['Make'] as string | undefined,
      cameraModel: parsed['Model'] as string | undefined,
      captureDate: captureDate instanceof Date ? captureDate : undefined,
      exposureBias: parsed['ExposureBiasValue'] as number | undefined,
    };
  }
}
